import {
	Box3,
	Matrix4,
	PerspectiveCamera,
	Quaternion,
	Raycaster,
	Sphere,
	Spherical,
	Vector2,
	Vector3,
	Vector4
} from 'three';

import {
	loadCameraControls,
	initCameraControlsThree,
	type CameraControlsType,
	type CameraControlsInstance
} from '../../../utils/lazy-camera-controls';
import type {
	CameraManagerConfig,
	FocalOffset,
	CameraOffset,
	FramingOptions,
	SceneDimensions
} from './types';
import {
	configureControlsCfn,
	configureConstraintsCfn,
	setupConstraintsCfn,
	setupKeyboardHandlersCfn,
	setOptimalDistanceCfn,
	setZoomCfn,
	rotateToAngleCfn,
	zoomToFitCfn,
	computeFocalOffsetFromAnchorCfn
} from './functions';
import type { ViewportAnchor } from './functions';

// Initialize camera-controls with Three.js subset (done once at module load)
// This keeps Three.js in the main bundle rather than the camera-controls chunk
initCameraControlsThree({
	Vector2,
	Vector3,
	Vector4,
	Quaternion,
	Matrix4,
	Spherical,
	Box3,
	Sphere,
	Raycaster
});

/**
 * CameraManager handles camera controls, positioning, and constraints.
 *
 * NOTE: camera-controls is lazy-loaded to reduce initial bundle size.
 * Use the static `create()` factory method to instantiate this class.
 *
 * Features:
 * - Orbital camera controls (rotate, pan, zoom)
 * - Optimal distance calculation based on scene dimensions
 * - Floor restriction (camera can't go below floor)
 * - Keyboard navigation (arrow keys)
 * - Focal offset for centering
 *
 * @example
 * ```typescript
 * // Use factory method (async) for lazy-loaded camera-controls
 * const cameraManager = await CameraManager.create({
 *   camera: camera,
 *   domElement: renderer.domElement
 * });
 *
 * cameraManager.setOptimalDistance({ width: 96, depth: 48, height: 36 }, 1.0);
 *
 * // In render loop
 * cameraManager.update(delta);
 *
 * // Cleanup
 * cameraManager.dispose();
 * ```
 */
export class CameraManager {
	#camera: PerspectiveCamera;
	#controls: CameraControlsInstance;
	#CameraControlsClass: CameraControlsType;
	#debugMode: boolean;
	#optimalDistance = 200;
	#focalOffset: FocalOffset = { x: 0, y: 0 };
	#viewportAnchor: ViewportAnchor = { vertical: 0.5, horizontal: 0.5 };
	#viewportAnchorUpdateHandler: (() => void) | null = null;
	#isFloorPresent: () => boolean = () => false;
	#floorRestrictionHandler: (() => void) | null = null;
	#keydownHandler: ((e: KeyboardEvent) => void) | null = null;
	#keyupHandler: ((e: KeyboardEvent) => void) | null = null;
	#keyState = {
		shiftRight: false,
		shiftLeft: false,
		controlRight: false,
		controlLeft: false
	};
	#cameraSource: Vector3 | null = null;
	#cameraTarget: Vector3 | null = null;
	#wakeHandler: (() => void) | null = null;
	#sleepHandler: (() => void) | null = null;
	#transitionStartHandler: (() => void) | null = null;

	/**
	 * Factory method to create CameraManager with lazy-loaded camera-controls
	 * @param config - Camera manager configuration
	 * @returns Promise resolving to CameraManager instance
	 */
	static async create(config: CameraManagerConfig): Promise<CameraManager> {
		const CameraControls = await loadCameraControls();
		return new CameraManager(config, CameraControls);
	}

	/**
	 * Private constructor - use CameraManager.create() instead
	 */
	private constructor(config: CameraManagerConfig, CameraControlsClass: CameraControlsType) {
		this.#camera = config.camera;
		this.#debugMode = config.debugMode ?? false;
		this.#CameraControlsClass = CameraControlsClass;

		// Create camera controls
		this.#controls = new CameraControlsClass(config.camera, config.domElement);
		this.#controls.enabled = true;

		// Configure controls
		configureControlsCfn(this, CameraControlsClass);

		// Setup constraints
		this.#floorRestrictionHandler = setupConstraintsCfn(this);

		// Setup keyboard handlers
		const handlers = setupKeyboardHandlersCfn(this, CameraControlsClass);
		this.#keydownHandler = handlers.keydownHandler;
		this.#keyupHandler = handlers.keyupHandler;

		// Setup viewport anchor update on camera updates (including animations)
		// Using 'update' event ensures anchor is maintained during programmatic transitions
		this.#viewportAnchorUpdateHandler = () => {
			// Re-apply viewport anchor when camera updates
			if (this.#viewportAnchor.vertical !== 0.5 || this.#viewportAnchor.horizontal !== 0.5) {
				this.applyViewportAnchor();
			}
		};
		this.#controls.addEventListener('update', this.#viewportAnchorUpdateHandler);
	}

	// ============ Public API ============

	/**
	 * Get the camera
	 */
	get camera(): PerspectiveCamera {
		return this.#camera;
	}

	/**
	 * Get the controls
	 */
	get controls(): CameraControlsInstance {
		return this.#controls;
	}

	/**
	 * Get/set debug mode
	 */
	get debugMode(): boolean {
		return this.#debugMode;
	}

	set debugMode(enabled: boolean) {
		this.#debugMode = enabled;
		configureConstraintsCfn(this);
	}

	/**
	 * Get the optimal distance
	 */
	get optimalDistance(): number {
		return this.#optimalDistance;
	}

	/**
	 * Get/set focal offset
	 */
	get focalOffset(): FocalOffset {
		return { ...this.#focalOffset };
	}

	set focalOffset(offset: FocalOffset) {
		this.#focalOffset = { ...offset };
		this.#controls.setFocalOffset(offset.x, offset.y, 0, false);
	}

	/**
	 * Get/set viewport anchor
	 */
	get viewportAnchor(): ViewportAnchor {
		return { ...this.#viewportAnchor };
	}

	set viewportAnchor(anchor: ViewportAnchor) {
		this.#viewportAnchor = { ...anchor };
		this.applyViewportAnchor();
	}

	/**
	 * Apply viewport anchor as focal offset.
	 * Called when anchor changes or when camera distance changes (zoom).
	 */
	private applyViewportAnchor(): void {
		const offset = computeFocalOffsetFromAnchorCfn({
			anchor: this.#viewportAnchor,
			camera: this.#camera,
			controls: this.#controls
		});
		this.#controls.setFocalOffset(offset.x, offset.y, 0, false);
	}

	/**
	 * Set the floor presence checker
	 */
	setFloorPresenceChecker(checker: () => boolean): void {
		this.#isFloorPresent = checker;
	}

	/**
	 * Set optimal camera distance based on scene dimensions
	 */
	// eslint-disable-next-line max-params -- `framing` is an optional 4th argument so existing callers keep working
	setOptimalDistance(
		dimensions: SceneDimensions,
		zoom: number,
		offset?: CameraOffset,
		framing?: FramingOptions
	): void {
		setOptimalDistanceCfn(this, { dimensions, zoom, offset, framing });
		// Re-apply viewport anchor after setting position
		if (this.#viewportAnchor.vertical !== 0.5 || this.#viewportAnchor.horizontal !== 0.5) {
			this.applyViewportAnchor();
		}
	}

	/**
	 * Set zoom level
	 */
	setZoom(zoom: number): void {
		setZoomCfn(this, { zoom });
	}

	/**
	 * Rotate camera to a specific angle
	 */
	async rotateToAngle(
		azimuthAngle: number,
		polarAngle?: number,
		duration: number = 1.0
	): Promise<void> {
		await rotateToAngleCfn(this, { azimuthAngle, polarAngle, duration });
	}

	/**
	 * Zoom to fit a bounding box
	 */
	zoomToFit(min: Vector3, max: Vector3, animate: boolean = true, paddingFactor: number = 1.2): void {
		zoomToFitCfn(this, { min, max, animate, paddingFactor });
		// Re-apply viewport anchor after setting position
		if (this.#viewportAnchor.vertical !== 0.5 || this.#viewportAnchor.horizontal !== 0.5) {
			this.applyViewportAnchor();
		}
	}

	/**
	 * Set camera position and target
	 */
	setPosition(
		posX: number,
		posY: number,
		posZ: number,
		targetX: number,
		targetY: number,
		targetZ: number,
		enableTransition: boolean = false
	): void {
		this.#controls.setLookAt(posX, posY, posZ, targetX, targetY, targetZ, enableTransition);
		// Re-apply viewport anchor after setting position
		if (this.#viewportAnchor.vertical !== 0.5 || this.#viewportAnchor.horizontal !== 0.5) {
			this.applyViewportAnchor();
		}
	}

	/**
	 * Set camera target
	 */
	setTarget(x: number, y: number, z: number, enableTransition: boolean = false): void {
		this.#controls.setTarget(x, y, z, enableTransition);
	}

	/**
	 * Get the current camera target
	 */
	getTarget(): Vector3 {
		const target = new Vector3();
		this.#controls.getTarget(target);
		return target;
	}

	/**
	 * Truck (pan) the camera
	 */
	truck(x: number, y: number, enableTransition: boolean = false): void {
		this.#controls.truck(x, y, enableTransition);
	}

	// ============ Wake / Sleep Events ============

	/**
	 * Register callbacks for camera activity lifecycle.
	 *
	 * - `controlstart` fires on mousedown/touchstart (independent of update())
	 * - `sleep` fires when all damping has fully settled (inside update())
	 *
	 * Uses `controlstart` instead of `wake` because `wake` only fires inside
	 * `update()` -- if the render loop is stopped, `update()` never runs and
	 * `wake` never fires, creating a deadlock.
	 *
	 * Only one pair of callbacks can be active at a time; calling this again
	 * will remove any previously registered pair.
	 *
	 * @param onWake  Called when user begins interacting with the camera
	 * @param onSleep Called when camera-controls has fully come to rest
	 */
	setWakeSleepCallbacks (onWake: () => void, onSleep: () => void): void {
		// Remove previous handlers if any
		this.removeWakeSleepCallbacks();

		// controlstart fires synchronously inside camera-controls' pointerdown
		// handler — BEFORE the render loop's first rAF runs. camera-controls
		// gates pointermove processing on an internal `_dragNeedsUpdate` flag
		// that is only re-armed at the end of update(). Between the prior
		// drag's endDragging (which sets it false) and the next update() tick,
		// any pointermoves are early-returned and the drag silently no-ops.
		// Calling update(0) here is a zero-delta no-op for camera state but
		// unconditionally re-arms _dragNeedsUpdate so the first pointermove of
		// a fresh drag is actually applied.
		const primedWake = (): void => {
			this.#controls.update(0);
			onWake();
		};

		this.#wakeHandler = primedWake;
		this.#sleepHandler = onSleep;
		// transitionstart fires for wheel zoom, keyboard arrows, and any
		// programmatic camera moves (dollyTo/truck/elevate). camera-controls
		// explicitly documents that wheel does NOT emit controlstart, so without
		// this we never wake the render loop on wheel zoom — the dolly is
		// queued internally and only renders the next time the user drags.
		this.#transitionStartHandler = primedWake;

		this.#controls.addEventListener('controlstart', this.#wakeHandler);
		this.#controls.addEventListener('transitionstart', this.#transitionStartHandler);
		this.#controls.addEventListener('sleep', this.#sleepHandler);
	}

	/**
	 * Remove previously registered wake/sleep callbacks.
	 */
	removeWakeSleepCallbacks (): void {
		if (this.#wakeHandler) {
			this.#controls.removeEventListener('controlstart', this.#wakeHandler);
			this.#wakeHandler = null;
		}

		if (this.#transitionStartHandler) {
			this.#controls.removeEventListener('transitionstart', this.#transitionStartHandler);
			this.#transitionStartHandler = null;
		}

		if (this.#sleepHandler) {
			this.#controls.removeEventListener('sleep', this.#sleepHandler);
			this.#sleepHandler = null;
		}
	}

	// ============ Camera Vector API ============

	/**
	 * Set the default camera vector (source and target) for vector-based zoom.
	 * Also sets distance constraints on the controls.
	 */
	setCameraVector(source: Vector3, target: Vector3): void {
		this.#cameraSource = source.clone();
		this.#cameraTarget = target.clone();
	}

	/**
	 * Get the camera source point (the far/wide-shot position)
	 */
	get cameraSource(): Vector3 | null {
		return this.#cameraSource?.clone() ?? null;
	}

	/**
	 * Get the camera target point (the look-at point)
	 */
	get cameraTargetPoint(): Vector3 | null {
		return this.#cameraTarget?.clone() ?? null;
	}

	/**
	 * Set distance constraints on the camera controls
	 */
	setDistanceConstraints(min: number, max: number): void {
		this.#controls.minDistance = min;
		this.#controls.maxDistance = max;
	}

	/**
	 * Update camera controls (call in render loop).
	 * @returns true if the camera has moved this frame
	 */
	update (delta: number): boolean {
		return this.#controls.update(delta);
	}

	/**
	 * Dispose of resources
	 */
	dispose (): void {
		// Remove wake/sleep handlers
		this.removeWakeSleepCallbacks();

		// Remove floor restriction handler
		if (this.#floorRestrictionHandler) {
			this.#controls.removeEventListener('update', this.#floorRestrictionHandler);
			this.#floorRestrictionHandler = null;
		}

		// Remove viewport anchor handler
		if (this.#viewportAnchorUpdateHandler) {
			this.#controls.removeEventListener('update', this.#viewportAnchorUpdateHandler);
			this.#viewportAnchorUpdateHandler = null;
		}

		// Remove keyboard handlers
		if (this.#keydownHandler) {
			window.removeEventListener('keydown', this.#keydownHandler);
			this.#keydownHandler = null;
		}

		if (this.#keyupHandler) {
			window.removeEventListener('keyup', this.#keyupHandler);
			this.#keyupHandler = null;
		}

		// Dispose controls
		this.#controls.dispose();
	}

	// ============ Internal Accessors (for CFN files) ============

	/**
	 * Access floor presence checker function
	 */
	isFloorPresentFn(): boolean {
		return this.#isFloorPresent();
	}

	/**
	 * Access key state
	 */
	get keyState(): {
		shiftRight: boolean;
		shiftLeft: boolean;
		controlRight: boolean;
		controlLeft: boolean;
	} {
		return this.#keyState;
	}

	/**
	 * Set optimal distance value (internal)
	 */
	setOptimalDistanceValue(value: number): void {
		this.#optimalDistance = value;
	}
}
